import { useState, useEffect, useCallback } from 'react';
import {
  Banknote, DollarSign, Users, TrendingUp, Check, Clock,
  Loader2, ChevronDown, AlertTriangle,
} from 'lucide-react';
import { Spinner } from '@/components/shared/UIComponents';
import {
  fetchAllPayouts, fetchAllReferrals, fetchPlatformReferralStats,
  processReferralPayout, createReferralPayout,
} from '@/lib/api';
import type { ReferralPayout, TenantReferral, PayoutStatus } from '@/types';

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function PayoutsPage() {
  const [payouts, setPayouts] = useState<ReferralPayout[]>([]);
  const [referrals, setReferrals] = useState<TenantReferral[]>([]);
  const [stats, setStats] = useState<{
    total_referrals: number; active_referrals: number;
    total_commissions_earned: number; pending_payouts: number; total_paid: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<PayoutStatus | 'all'>('all');
  const [processing, setProcessing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r, s] = await Promise.all([
        fetchAllPayouts(),
        fetchAllReferrals(),
        fetchPlatformReferralStats(),
      ]);
      setPayouts(p);
      setReferrals(r);
      setStats(s);
    } catch (err) {
      console.error('Failed to load payouts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (payoutId: string, newStatus: PayoutStatus) => {
    setProcessing(payoutId);
    try {
      await processReferralPayout(payoutId, { status: newStatus });
      await load();
    } finally {
      setProcessing(null);
    }
  };

  const filtered = filter === 'all' ? payouts : payouts.filter(p => p.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-surface-900">Referral Payouts</h1>
        <p className="text-sm text-surface-500 mt-1">Manage referral commissions and payouts across the platform</p>
      </div>

      {/* Honest disclosure: this page is a tracking ledger, not an
          automated payout system. Three pieces of automation are
          missing and surfaced here so neither the current operator
          nor any future admin assumes "Mark Paid" actually moved
          money. Mirrors the manual-fulfillment warning pattern on
          the tenant-admin Affiliates page. */}
      <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg flex gap-3">
        <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <div className="font-semibold mb-1">Manual ledger — no auto-payouts yet</div>
          <p className="leading-relaxed">
            This page <strong>tracks</strong> referral commissions and payouts. It does <strong>not</strong>{' '}
            calculate, schedule, or transfer money. Until the three automation pieces below ship, you (the
            super-admin) must do each step by hand and use this page to record what you did.
          </p>
          <ul className="mt-3 space-y-1.5 list-disc list-inside leading-relaxed">
            <li>
              <strong>Commissions are not auto-created.</strong> No Stripe webhook is wired, so when a referred
              tenant pays a subscription invoice, no row appears in <code className="text-[12px] bg-amber-100 px-1 rounded">referral_commissions</code>{' '}
              automatically. Add commission rows manually if you want them tracked.
            </li>
            <li>
              <strong>Payouts are not auto-rolled-up.</strong> There is no monthly cron that sums earned
              commissions into a payout row. Use the <code className="text-[12px] bg-amber-100 px-1 rounded">createReferralPayout</code>{' '}
              helper directly (or insert via Supabase) when you decide to pay a referrer.
            </li>
            <li>
              <strong>"Mark Paid" does not send money.</strong> The button only flips the row's status —
              it does <strong>not</strong> call Stripe Connect Transfers or any payment API. You must send the
              actual payment yourself (Stripe Dashboard, bank transfer, PayPal), then click Mark Paid to record it.
            </li>
          </ul>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: 'Total Referrals', value: stats.total_referrals, icon: Users },
            { label: 'Active Referrals', value: stats.active_referrals, icon: TrendingUp },
            { label: 'Commissions Earned', value: formatCents(stats.total_commissions_earned), icon: DollarSign },
            { label: 'Pending Payouts', value: formatCents(stats.pending_payouts), icon: Clock },
            { label: 'Total Paid', value: formatCents(stats.total_paid), icon: Check },
          ].map(s => (
            <div key={s.label} className="glass-panel p-4">
              <div className="flex items-center gap-2 mb-1">
                <s.icon size={14} className="text-surface-400" />
                <span className="text-xs text-surface-500">{s.label}</span>
              </div>
              <div className="text-lg font-bold text-surface-900">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Referrals Overview */}
      {referrals.length > 0 && (
        <div className="glass-panel overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-300">
            <h3 className="font-semibold text-surface-900">All Referral Relationships</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-surface-300/60 text-left text-surface-500">
                <th className="px-5 py-3 font-medium">Referred Tenant</th>
                <th className="px-5 py-3 font-medium">Code Used</th>
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium">Status</th>
              </tr></thead>
              <tbody>
                {referrals.slice(0, 20).map(r => (
                  <tr key={r.id} className="border-b border-surface-300/30 hover:bg-surface-100/50">
                    <td className="px-5 py-3 font-medium text-surface-900">{r.referred_tenant?.name || '—'}</td>
                    <td className="px-5 py-3 font-mono text-surface-500 text-xs">{r.referral_code_used}</td>
                    <td className="px-5 py-3 text-surface-500">{formatDate(r.referred_at)}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        r.status === 'active' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                        r.status === 'pending' ? 'badge-amber' :
                        'bg-surface-300/30 text-surface-500'
                      }`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payouts Table */}
      <div className="glass-panel overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-300 flex items-center justify-between">
          <h3 className="font-semibold text-surface-900">Payouts</h3>
          <div className="flex gap-1">
            {(['all', 'pending', 'processing', 'paid', 'failed'] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  filter === f ? 'badge-brand' : 'text-surface-500 hover:text-surface-700 hover:bg-surface-300/30'
                }`}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-12 text-surface-500">
            <Banknote size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">{filter === 'all' ? 'No payouts yet' : `No ${filter} payouts`}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-surface-300/60 text-left text-surface-500">
                <th className="px-5 py-3 font-medium">Tenant</th>
                <th className="px-5 py-3 font-medium">Amount</th>
                <th className="px-5 py-3 font-medium">Method</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium">Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} className="border-b border-surface-300/30 hover:bg-surface-100/50">
                    <td className="px-5 py-3 font-medium text-surface-900">{p.referrer_tenant?.name || '—'}</td>
                    <td className="px-5 py-3 font-semibold text-surface-900">{formatCents(p.amount)}</td>
                    <td className="px-5 py-3 text-surface-500 capitalize">{p.payout_method.replace(/_/g, ' ')}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        p.status === 'paid' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                        p.status === 'processing' ? 'badge-blue' :
                        p.status === 'pending' ? 'badge-amber' :
                        'badge-red'
                      }`}>{p.status}</span>
                    </td>
                    <td className="px-5 py-3 text-surface-500">{formatDate(p.created_at)}</td>
                    <td className="px-5 py-3">
                      {p.status === 'pending' && (
                        <button onClick={() => handleStatusChange(p.id, 'processing')} disabled={processing === p.id}
                          className="text-xs text-brand-500 hover:text-brand-600 font-medium">
                          {processing === p.id ? <Loader2 size={12} className="animate-spin" /> : 'Process'}
                        </button>
                      )}
                      {p.status === 'processing' && (
                        <button onClick={() => handleStatusChange(p.id, 'paid')} disabled={processing === p.id}
                          className="text-xs text-green-600 hover:text-green-700 font-medium">
                          {processing === p.id ? <Loader2 size={12} className="animate-spin" /> : 'Mark Paid'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
